import React, { Fragment, useContext, useMemo } from "react"
import { useStaticQuery, graphql } from "gatsby"
import FlexTable, { FlexTableCell } from "../flex-table"
import Link from "../link"
import CheckThickIcon from "../../icons/check-thick"
import { UserInterfaceContext } from "../../context/UserInterfaceContext"
import classNames from "../../utils/classnames"
import GcStartFilter from "../gc-start-filter"
import FancyImage from "../fancy-image"
import GridIcon from "../../icons/grid"
import ListIcon from "../../icons/list"
import { campusUrlFromName } from "../../utils/georgian"
import isBrowser from "../../utils/isBrowser"
import CloseThickIcon from "../../icons/close-thick"
import GcCampusFilter from "../gc-campus-filter"
import GcAreaFilter from "../gc-area-filter"
import GcKeywordFilter from "../gc-keyword-filter"
import GcDeliveryFilter from "../gc-delivery-filter"
import GcCredentialFilter from "../gc-credential-filter"
import StarIconComponent from "../star-icon"
import NoFavoriteProgramsMessage from "../favorite-programs/no-favorite-programs-message"

export default function ProgramListingFavorite(props): JSX.Element {
  const { partTimeOnly } = props
  const debug = isBrowser
    ? window.localStorage.getItem(`gcdebug`) === `true`
    : false
  const [gridView, setGridView] = React.useState(true)
  const [isClient, setIsClient] = React.useState(false)

  React.useEffect(() => {
    setIsClient(true)
  }, [])

  const programListQuery: Queries.GcProgramListQuery = useStaticQuery(graphql`
    query GcProgramList {
      allGcProgram(filter: { showInList: { eq: true }, hasOverridingProgram: { eq: false } }) {
        totalCount
        nodes {
          title
          programCode
          dataSource
          catalogProgram {
            customFieldsJson
          }
          sourceProgram {
            ... on CimcatProgram {
              locations
              coop
              delivery
              credential
              academicarea
              academicArea
              intakes {
                year
                semester
                suspended
                campuses {
                  name
                  url
                }
              }
            }
            ... on D1Certificate {
              id
              credential: category
            }
            ... on PtsProgram {
              Campuses
              credential: Credential
            }
          }
        }
      }
      nonBannerPrograms: allWpOldProgram(
        filter: { customFieldsJson: { regex: "/non_banner\":true/" } }
      ) {
        nodes {
          title
          customFieldsJson
          featuredImageSrc
          featuredImageAlt
        }
      }
    }
  `)

  const { gcProgramFilters, setGcProgramFilter } =
    useContext(UserInterfaceContext)

  const ccPrograms = programListQuery?.allGcProgram?.nodes
    ?.filter(n => n.dataSource === `CC`)
    .map(n => {
      const customFields = JSON.parse(n.catalogProgram.customFieldsJson)
      return {
        dataSource: n.dataSource,
        title: n.title,
        programCode: n.programCode,
        delivery: n.sourceProgram?.delivery,
        credential: n.sourceProgram?.credential,
        academic_area:
          customFields?.academic_area ??
          n.sourceProgram?.academicArea ??
          n.sourceProgram?.academicarea,
        intakes: n.sourceProgram?.intakes,
        coop: n.sourceProgram?.coop,
        url: `/academics/programs/${customFields.slug}/`,
        nonBanner: false,
        locations: n.sourceProgram?.locations,
        image_small: customFields?.image_small,
      }
    })

  const d1Programs = programListQuery?.allGcProgram?.nodes
    ?.filter(n => n.dataSource === `D1`)
    .map(n => {
      const customFields = JSON.parse(n.catalogProgram.customFieldsJson)
      return {
        dataSource: n.dataSource,
        title: n.title,
        programCode: n.programCode,
        delivery: `Part-time`,
        credential: n.sourceProgram?.credential,
        academic_area: customFields?.academic_area ?? ``,
        intakes: [],
        coop: false,
        url: `/academics/programs/${customFields.slug}/`,
        nonBanner: false,
        locations: `See program page for more information`,
        image_small: customFields?.image_small,
      }
    })

  const ptsPrograms = programListQuery?.allGcProgram?.nodes
    ?.filter(n => n.dataSource === `PTS`)
    .map(n => {
      const customFields = JSON.parse(n.catalogProgram.customFieldsJson)
      return {
        dataSource: n.dataSource,
        title: n.title,
        programCode: n.programCode,
        delivery: `Part-time`,
        credential: n.sourceProgram?.credential,
        academic_area:
          customFields?.academic_area ??
          n.sourceProgram?.academicArea ??
          n.sourceProgram?.academicarea,
        intakes: [],
        coop: false,
        url: `/academics/programs/${customFields.slug}/`,
        nonBanner: false,
        locations: n.sourceProgram?.Campuses?.join(`,`),
        image_small: customFields?.image_small,
      }
    })

  let programs = [...ccPrograms, ...d1Programs, ...ptsPrograms]

  if (partTimeOnly) {
    programs = programs.filter(program =>
      program.delivery?.toLowerCase().includes(`part`)
    )
  }

  const total = programs.length

  programs = programs.sort((a, b) =>
    a.title < b.title ? -1 : a.title > b.title ? 1 : 0
  )

  const favoritePrograms = isClient
    ? programs.filter(
      program =>
        localStorage.getItem(
          `favoriteGeorgianProgram_${program.programCode}`
        ) === `true`
    )
    : []

  return (
    <div className="border-none">
      {isClient && favoritePrograms.length > 0 && (
        <div className="flex gap-4 items-center mb-4">
          <span>View: </span>
          <button
            onClick={() => setGridView(false)}
            title="Switch to list view"
          >
            <ListIcon
              className={classNames(
                `w-8 h-8 hover:text-blue`,
                !gridView ? `text-blue` : `text-black`
              )}
            />
          </button>
          <button onClick={() => setGridView(true)} title="Switch to grid view">
            <GridIcon
              className={classNames(
                `w-8 h-8 hover:text-blue`,
                gridView ? `text-blue` : `text-black`
              )}
            />
          </button>
        </div>
      )}

      {isClient ? (
        favoritePrograms.length > 0 ? (
          gridView ? (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {favoritePrograms.map(program => (
                <div
                  className="relative row-span-2 col-span-1 flex flex-col gap-4 group"
                  key={program.slug}
                >
                  <div className="absolute top-5 right-5 z-10  bg-white rounded-full bg-opacity-75 hover:bg-opacity-100 transition-all duration-300 ease-in-out cursor-pointer">
                    <StarIconComponent id={program.programCode} />
                  </div>
                  <Link external={program.nonBanner} to={program.url}>
                    <FancyImage
                      src={program?.image_small?.sizes?.large}
                      alt={program?.image_small?.alt}
                    />
                  </Link>
                  <div className="bg-lightgray aspect-square p-4">
                    <Link
                      external={program.nonBanner}
                      to={program.url}
                      className="flex flex-col gap-1 text-black no-underline"
                    >
                      <h3>
                        <a className="no-underline">{program.title}</a>
                      </h3>
                      <span className="flex gap-4">
                        <strong className="w-2/5">Location(s): </strong>
                      </span>
                      <span className="ml-4">{program.locations}</span>
                      <span className="flex gap-4">
                        <strong className="w-2/5">Delivery: </strong>
                      </span>
                      <span className="ml-4">{program.delivery}</span>
                      <span className="flex flex-col">
                        <strong className="">Credential: </strong>
                      </span>
                      <span className="ml-4">{program.credential}</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <FlexTable
              rows={favoritePrograms.map(program => [
                <StarIconComponent
                  key={`${program.programCode}-star`}
                  id={program.programCode}
                />,
                <Link
                  key={`${program.programCode}-title`}
                  external={program.nonBanner}
                  to={program.url}
                >
                  {program.title}
                  {debug && ` (${program.dataSource})`}
                </Link>,
                <div
                  className={classNames(
                    `flex flex-col divide-y gap-2 divide-lightgray`,
                    program.dataSource !== `D1` ? `capitalize` : ``
                  )}
                  key={`${program.programCode}-locations`}
                >
                  {program.locations}
                </div>,
                <div
                  className="flex flex-col divide-y gap-2 divide-lightgray"
                  key={`${program.programCode}-delivery`}
                >
                  {program.delivery}
                </div>,
                <div
                  className="flex flex-col divide-y gap-2 divide-lightgray"
                  key={`${program.programCode}-credential`}
                >
                  {program.credential}
                </div>,
              ])}
              headings={[
                { label: `🔖`, width: `w-1/12` },
                { label: `Title`, width: `w-4/12` },
                { label: `Location(s)`, width: `w-2/12` },
                { label: `Delivery`, width: `w-2/12` },
                { label: `Credential`, width: `w-3/12` },
              ]}
              footer={
                <p>
                  Showing {favoritePrograms.length} of {total} programs
                </p>
              }
            />
          )
        ) : (
          <Fragment>
            <NoFavoriteProgramsMessage />
          </Fragment>
        )
      ) : null}
    </div>
  )
}
